'use client';

import { T } from '@/i18n/LocaleProvider';
import { MONO_FONT } from '@/theme';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, PageSection } from '@/components';
import Link from 'next/link';

export default function Agent() {
  return (
    <PageSection
      id="agente"
      eyebrow="04 / Agente Integrado"
      title={
        <>
          <T>Tu copiloto AEO,</T>{' '}
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <span className="accent">
            <T>siempre disponible.</T>
          </span>
        </>
      }
      lede="Le pides en lenguaje natural y ejecuta dentro de tu workspace: auditar schema, explicar una caída, publicar tu llms.txt. También disponible como servidor MCP para Claude, Cursor o tu agente propio."
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: 4,
          alignItems: 'stretch',
        }}
      >
        {/* Chat mock */}
        <Box
          sx={{
            minWidth: 0,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 3.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
            bgcolor: 'background.default',
          }}
        >
          {/* Card header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 1.5,
              pb: 1.75,
              borderBottom: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '10.5px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'primary.darker',
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                <T>BISON AGENT · INTEGRADO</T>
              </Typography>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                }}
              >
                <T>Conversación en el dashboard</T>
              </Typography>
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '10.5px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.accent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  bgcolor: 'primary.main',
                  borderRadius: '50%',
                  boxShadow: '0 0 0 3px rgba(0,159,139,0.18)',
                  animation: 'pulse 2.4s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
              <T>ACTIVO</T>
            </Box>
          </Box>

          {/* Chat messages */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              fontSize: '14px',
              mt: 1,
            }}
          >
            {/* User */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: 'grey.100',
                  color: 'grey.700',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: MONO_FONT,
                  fontSize: '10px',
                  fontWeight: 600,
                }}
              >
                <T>TÚ</T>
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'grey.800',
                  lineHeight: 1.6,
                  pt: '3px',
                }}
              >
                <T>Mi CMO pregunta por qué bajamos en ChatGPT este mes. </T>
                <br />
                <T>Dame los 3 motivos en orden de impacto.</T>
              </Typography>
            </Box>

            {/* Bison */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: MONO_FONT,
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                <T>B</T>
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'grey.800',
                  lineHeight: 1.6,
                  pt: '3px',
                }}
              >
                <T>Tres causas, mayor a menor impacto:</T>
                <br />
                <br />
                <strong>1.</strong>
                <T>
                  {' '}
                  Tu competidor #2 publicó un caso de uso el 4 de mayo que el
                  motor está priorizando para 18 de tus prompts
                </T>
                <T> </T>
                <Box component="span" sx={{ color: 'primary.darker' }}>
                  <T>−9 pts SoV</T>
                </Box>
                .
                <br />
                <strong>2.</strong>
                <T> Tu</T>
                <T> </T>
                <Box
                  component="code"
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '12px',
                    bgcolor: 'grey.100',
                    px: '5px',
                    py: '1px',
                    borderRadius: '3px',
                  }}
                >
                  <T>llms.txt</T>
                </Box>
                <T> </T>
                <T>apunta a /docs/v1 que ya no existe (error 404).</T>
                <br />
                <strong>3.</strong>
                <T>
                  {' '}
                  Schema en /pricing falla intermitentemente desde el último
                  deploy.
                </T>
                <br />
                <br />
                <T>¿Genero el brief para tu equipo o lo corrijo directo?</T>
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, mb: 1 }}
          >
            <Button variant="contained" size="small">
              <T>Agendar acciones</T>
            </Button>
            <Button variant="ghost" size="small">
              <T>Generar brief</T>
            </Button>
            <Button
              variant="ghost"
              size="small"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              <T>Exportar a slides</T>
            </Button>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              pt: 1.75,
              borderTop: '1px solid',
              borderColor: 'grey.200',
              fontFamily: MONO_FONT,
              fontSize: '11px',
              color: 'text.muted',
              letterSpacing: '0.04em',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <span>
              <T>OPERÓ · 6 ACCIONES · 3.4s</T>
            </span>
            <span>
              <T>CLAUDE OPUS 4.8 · TRAZABLE</T>
            </span>
          </Box>
        </Box>

        {/* MCP dark card */}
        <Box
          data-surface="dark"
          sx={{
            minWidth: 0,
            bgcolor: 'grey.950',
            borderRadius: 2,
            p: 3.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
          }}
        >
          {/* Card header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 1.5,
              pb: 1.75,
              borderBottom: '1px solid',
              borderColor: 'grey.900',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '10.5px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'primary.glow',
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                <T>BISON MCP · SERVIDOR</T>
              </Typography>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: 'common.white',
                }}
              >
                <T>Opera Bison desde tu propio agente</T>
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '10.5px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'primary.glow',
                whiteSpace: 'nowrap',
              }}
            >
              <T>PLAN Growth</T>
            </Typography>
          </Box>

          <Typography
            sx={{ fontSize: '14px', lineHeight: 1.6, color: 'grey.300' }}
          >
            <T>Implementamos el</T>
            <T> </T>
            <Box component="strong" sx={{ color: 'common.white' }}>
              <T>Model Context Protocol</T>
            </Box>
            <T>
              . Tu agente —Claude Desktop, Cursor, ChatGPT Team o uno propio— se
              conecta a Bison y consulta tu workspace como herramienta nativa.
            </T>
          </Typography>

          <Box
            component="pre"
            sx={{
              m: 0,
              bgcolor: 'grey.900',
              border: '1px solid',
              borderColor: 'grey.800',
              borderRadius: '6px',
              p: 2,
              fontFamily: MONO_FONT,
              fontSize: '12px',
              lineHeight: 1.7,
              color: 'grey.200',
              overflowX: 'auto',
              whiteSpace: 'pre',
            }}
          >
            <Box component="span" sx={{ color: 'text.muted' }}>
              <T>{'$ claude mcp add --transport http bison-agents \\\n   '}</T>
            </Box>
            <T> </T>
            <Box component="span" sx={{ color: '#c0e8d8' }}>
              <T>https://mcp.bisonagents.com</T>
            </Box>
            <T>{'\n\n'}</T>
            <Box component="span" sx={{ color: 'primary.glow' }}>
              ✓
            </Box>
            <T> </T>
            <Box component="span" sx={{ color: 'common.white' }}>
              <T>Connected</T>
            </Box>
            <T>{' · 34 tools · 4 resources · 3 prompts'}</T>
          </Box>

          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {[
              { name: 'get_visibility_summary', args: '(model)' },
              { name: 'compare_competitors', args: '(model)' },
              { name: 'run_schema_audit', args: '(url)' },
              { name: 'create_prompt', args: '(prompt, country)' },
            ].map((tool) => (
              <Box
                component="li"
                key={tool.name}
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '11.5px',
                  color: 'grey.300',
                  py: 1,
                  borderTop: '1px solid',
                  borderColor: 'grey.800',
                  letterSpacing: '0.02em',
                }}
              >
                <T>{tool.name}</T>
                <Box component="span" sx={{ color: 'text.muted' }}>
                  <T>{tool.args}</T>
                </Box>
              </Box>
            ))}
          </Box>

          <Link
            href="/tecnologia#agente"
            aria-label="Ver implementación técnica"
            style={{
              fontFamily: MONO_FONT,
              fontSize: '11.5px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--teal-glow)',
              textDecoration: 'none',
              marginTop: 'auto',
              paddingTop: '8px',
            }}
          >
            <T>Ver implementación técnica →</T>
          </Link>
        </Box>
      </Box>
    </PageSection>
  );
}
